// The agent loop: model -> tool calls -> site adapter -> model, until the model answers.
//
// A turn can outlive the page it started on: a tool may navigate (bowdencloud.com's go_to) or
// reload (MIDI's one-time connect), which unloads this script mid-step. The widget saves the
// history first, and the next page calls continueTurn(), which answers the tool calls still
// waiting for a result and carries on from there.
import { streamChat } from './openai.js';

const MAX_TOOL_RESULT_CHARS = 24000;
const HISTORY_BUDGET_CHARS = 160000;

const COMMON_PROMPT = `

General
- Be concise. Use short markdown (bold, lists, \`code\`) when it helps.
- You are a chat bar docked on the page itself; the page around it is what the user sees.`;

const SCREENSHOT_PROMPT = `
- You can call take_screenshot to see what the user sees (it is attached as an image after the tool result). Use it when the user refers to something visual, or to check your work when the data alone is not enough. Don't take screenshots needlessly.`;

const COMMON_TOOLS = [
  {
    type: 'function',
    function: {
      name: 'take_screenshot',
      description: 'Capture the visible part of the page the user is looking at.',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
  },
];

export const INTERRUPTED = 'Error: Interrupted: the page was closed or reloaded before this step finished.';

/** Messages as sent to the API: private fields removed, old screenshots dropped, oldest turns trimmed to budget. */
export function prepareMessages(history, budget = HISTORY_BUDGET_CHARS) {
  // group into turns, each starting at a message the user typed
  const turns = [];
  for (const m of history) {
    if (m._kind === 'error') continue; // UI-only note
    const out = {};
    for (const [k, v] of Object.entries(m)) if (!k.startsWith('_')) out[k] = v;
    if ((m.role === 'user' && !m._kind) || !turns.length) turns.push([out]);
    else turns[turns.length - 1].push(out);
  }
  // only the current turn keeps its screenshots
  for (const turn of turns.slice(0, -1)) {
    for (const m of turn) {
      if (Array.isArray(m.content)) m.content = m.content.map((p) => (p.type === 'image_url' ? { type: 'text', text: '[earlier screenshot removed]' } : p));
    }
  }
  const size = (t) => JSON.stringify(t).length;
  let total = turns.reduce((n, t) => n + size(t), 0);
  while (turns.length > 1 && total > budget) total -= size(turns.shift());
  return turns.flat();
}

function truncate(text) {
  if (text.length <= MAX_TOOL_RESULT_CHARS) return text;
  return text.slice(0, MAX_TOOL_RESULT_CHARS) + `\n…[truncated ${text.length - MAX_TOOL_RESULT_CHARS} chars — narrow the request]`;
}

const abortError = () => Object.assign(new Error('Stopped'), { name: 'AbortError' });

/** Tool calls of the latest step that have no result yet (the page went away while they ran). */
export function pendingCalls(history) {
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i];
    if (m.role === 'tool') continue;
    if (m.role === 'assistant' && !m._kind && m.tool_calls?.length) {
      const done = new Set(history.slice(i + 1).map((x) => x.tool_call_id));
      return m.tool_calls.filter((c) => !done.has(c.id));
    }
    return [];
  }
  return [];
}

/** Model steps already taken in the current turn (since the last message the user typed). */
export function stepsInTurn(history) {
  let n = 0;
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i];
    if (m.role === 'user' && !m._kind) break;
    if (m.role === 'assistant' && !m._kind) n++;
  }
  return n;
}

/**
 * Give every tool call that never got a result an "interrupted" one, so the history is valid
 * for the API again. Returns how many were added. Mutates `history`.
 */
export function repairHistory(history) {
  let added = 0;
  for (let i = 0; i < history.length; i++) {
    const m = history[i];
    if (m.role !== 'assistant' || !m.tool_calls?.length) continue;
    let j = i + 1;
    const done = new Set();
    while (j < history.length && history[j].role === 'tool') done.add(history[j++].tool_call_id);
    const missing = m.tool_calls.filter((c) => !done.has(c.id)).map((c) => ({ role: 'tool', tool_call_id: c.id, content: INTERRUPTED }));
    if (missing.length) {
      history.splice(j, 0, ...missing);
      added += missing.length;
      i = j + missing.length - 1;
    }
  }
  return added;
}

/**
 * Run one user turn. Mutates `history` (array of chat messages without the system prompt).
 * emit(type, data): 'status' | 'assistant_start' | 'text' | 'assistant_end' | 'tool_start' | 'tool_end'
 */
export async function runTurn(opts) {
  opts.history.push({ role: 'user', content: opts.userText });
  return continueTurn(opts);
}

/**
 * Carry the current turn on from wherever `history` stands.
 * `ctx` is handed to the site (navigation, links); `capture()` takes a screenshot, or is null when screenshots are off.
 */
export async function continueTurn({ settings, site, ctx = {}, history, signal, emit = () => {}, capture = null }) {
  emit('status', `Connecting to ${site.name}…`);
  await site.prepare((msg) => emit('status', msg), ctx);

  const tools = capture ? [...site.tools, ...COMMON_TOOLS] : site.tools;
  const maxSteps = Math.max(1, Number(settings.maxSteps) || 24);

  const pending = pendingCalls(history);
  if (pending.length) {
    emit('status', 'Working…');
    await runCalls(pending, { site, ctx, history, signal, emit, capture });
  }

  for (let step = stepsInTurn(history); step < maxSteps; step++) {
    if (signal?.aborted) throw abortError();
    emit('status', 'Thinking…');
    const system = (await site.systemPrompt(ctx)) + COMMON_PROMPT + (capture ? SCREENSHOT_PROMPT : '');
    const messages = [{ role: 'system', content: system }, ...prepareMessages(history)];

    emit('assistant_start');
    let reply;
    let partial = '';
    try {
      reply = await streamChat({
        settings,
        messages,
        tools,
        signal,
        onText: (t) => {
          partial += t;
          emit('text', t);
        },
      });
    } catch (e) {
      if (e.name === 'AbortError' && partial) {
        const msg = { role: 'assistant', content: partial + ' …(stopped)' };
        history.push(msg);
        emit('assistant_end', msg);
      }
      throw e;
    }

    const msg = { role: 'assistant', content: reply.content || null };
    if (reply.toolCalls.length) {
      msg.tool_calls = reply.toolCalls.map((tc, i) => ({
        id: tc.id || `call_${Date.now()}_${i}`,
        type: 'function',
        function: { name: tc.name, arguments: tc.arguments || '{}' },
      }));
    }
    history.push(msg);
    emit('assistant_end', msg);
    if (!msg.tool_calls) return msg;
    await runCalls(msg.tool_calls, { site, ctx, history, signal, emit, capture });
  }

  const msg = { role: 'assistant', content: `I stopped after ${maxSteps} steps. Say "continue" if you want me to keep going.` };
  history.push(msg);
  emit('assistant_end', msg);
  return msg;
}

async function runCalls(calls, { site, ctx, history, signal, emit, capture }) {
  const images = [];
  for (const call of calls) {
    if (signal?.aborted) {
      history.push({ role: 'tool', tool_call_id: call.id, content: 'Cancelled: the user pressed stop.' });
      continue;
    }
    const name = call.function.name;
    let args = {};
    let result;
    let ok = true;
    try {
      args = JSON.parse(call.function.arguments || '{}');
    } catch {
      ok = false;
      result = `Error: arguments were not valid JSON: ${call.function.arguments}`;
    }
    emit('tool_start', { id: call.id, name, args });
    if (ok) {
      try {
        if (name === 'take_screenshot') {
          if (!capture) throw new Error('Screenshots are not enabled on this site.');
          images.push(await capture());
          result = 'Screenshot captured; it is attached in the next message.';
        } else {
          const value = await site.execute(name, args, { ...ctx, callId: call.id });
          result = typeof value === 'string' ? value : JSON.stringify(value);
        }
      } catch (e) {
        ok = false;
        result = `Error: ${e.message ?? e}`;
      }
    }
    result = truncate(result ?? '');
    history.push({ role: 'tool', tool_call_id: call.id, content: result });
    emit('tool_end', { id: call.id, name, ok, result });
  }
  for (const url of images) {
    history.push({
      role: 'user',
      _kind: 'screenshot',
      content: [
        { type: 'text', text: 'Screenshot from take_screenshot:' },
        { type: 'image_url', image_url: { url, detail: 'high' } },
      ],
    });
  }
}

/** Copy of history safe to persist: screenshots replaced by a placeholder. */
export function compactHistory(history) {
  return history.map((m) =>
    Array.isArray(m.content) ? { ...m, content: m.content.map((p) => (p.type === 'image_url' ? { type: 'text', text: '[earlier screenshot removed]' } : p)) } : m,
  );
}
